import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import SalesInvoiceRegisterForm from '../components/SalesInvoiceRegisterForm';
import { useGetSalesInvoiceRegisterHandler } from '../dataHandlers/getSalesInvoiceRegisterHandler';
import { useUpdateSalesInvoiceRegisterHandler } from '../dataHandlers/updateSalesInvoiceRegisterHandler';
import DocumentSkeletonForm from '../../../components/DocumentSkeletonForm';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_SALE_REGISTER } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function EditSalesInvoiceRegisterPage() {
  const { salesInvoiceRegister } = useTranslatedMessages();
  useDocumentTitle(salesInvoiceRegister?.pages?.edit_title ?? 'Edit Sales Invoice Register Entry');
  const navigate = useNavigate();
  const { document_id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const { register, isLoading } = useGetSalesInvoiceRegisterHandler(document_id);

  useEffect(() => {
    if (loading) return;
    if (!hasEInvoicePermission(UPDATE_SALE_REGISTER)) {
      showActionDeniedSnackbar();
      navigate('/sales-invoice-register', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!isLoading && register && register.document_mode !== 'NON_E_INVOICE') {
      const documentId = register.uuid || register.id;
      if (register.document_mode === 'E_INVOICE') {
        navigate(`/sales/documents/invoices/${documentId}`, { replace: true });
      } else {
        navigate('/sales-invoice-register', { replace: true });
      }
    }
  }, [isLoading, register, navigate]);

  const onRegisterUpdated = () => {
    navigate(`/sales-invoice-register`, { replace: true });
  };

  const { onSubmit, error, isPending } = useUpdateSalesInvoiceRegisterHandler({ onSuccess: onRegisterUpdated });

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const onSaveForm = (data) => {
    setIsSubmitting(true);
    onSubmit({ id: document_id, payload: data });
  };

  const onCancel = () => {
    navigate(`/sales-invoice-register`);
  };

  if (isLoading || !register) {
    return <DocumentSkeletonForm />;
  }

  if (register.document_mode !== 'NON_E_INVOICE') {
    return <DocumentSkeletonForm />;
  }

  return (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <SalesInvoiceRegisterForm
        initialData={register}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={error}
        isLoading={isPending || isSubmitting}
      />
    </Stack>
  );
}
